import React, { useEffect, useState } from "react";
import { Modal, Form, Input, Button, Space, Upload, message, Skeleton, Empty } from "antd";
import Select from "../../../shared/components/searchable-select.component";
import { UploadOutlined } from "@ant-design/icons";
import { useForm } from "antd/lib/form/Form";
import { Client } from "../../../core/api-client";
import { API_GET_ALL_DOCUMENT_SUB_TYPE_LIST, API_GET_ALL_DOCUMENT_SUB_TYPE_LIST_BY_DOCUMENT_TYPE, API_UPLOAD_DOCUMENT } from "../../../shared/routes/private.routes";
import { DocumentType, DocumentSubType } from "../../../shared/types/common";
import { showNotification } from "../../../shared/utils/utils";

interface UploadDocumentModalProps {
  visible: boolean;
  onClose: () => void;
  staffId: string;
  documentTypes: DocumentType[];
  onSubmit: (documentTypeId: string) => void;
  relatedRecordType?: string;
  relatedRecordId?: string;
  title?: string;
}

const UploadDocumentModal: React.FC<UploadDocumentModalProps> = ({
  visible,
  onClose,
  staffId,
  documentTypes,
  onSubmit,
  relatedRecordType,
  relatedRecordId,
  title = "Upload Document",
}) => {
  const [form] = useForm();
  const [selectedDocumentTypeId, setSelectedDocumentTypeId] = useState<string | null>(null);
  const [documentSubTypes, setDocumentSubTypes] = useState<DocumentSubType[]>([]);
  const [loadingDocumentSubTypes, setLoadingDocumentSubTypes] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false); // For future API integration

  // Reset form and states when modal opens/closes
  useEffect(() => {
    if (!visible) {
      form.resetFields();
      setSelectedDocumentTypeId(null);
      setDocumentSubTypes([]);
      setSelectedFiles([]);
    }
  }, [visible, form]);

  // Fetch document sub-types when a document type is selected.
  useEffect(() => {
    if (!selectedDocumentTypeId) {
      setDocumentSubTypes([]);
      return;
    }

    const fetchDocumentSubTypes = async () => {
      setLoadingDocumentSubTypes(true);
      try {
        const response = await Client.get(`${API_GET_ALL_DOCUMENT_SUB_TYPE_LIST_BY_DOCUMENT_TYPE}/${selectedDocumentTypeId}`);
        setDocumentSubTypes(response?.data?.data || []);
      } catch (error) {
        console.error("Failed to fetch document sub types:", error);
        showNotification("Failed to load document sub types.", "error");
        setDocumentSubTypes([]);
      } finally {
        setLoadingDocumentSubTypes(false);
      }
    };

    fetchDocumentSubTypes();
  }, [selectedDocumentTypeId]);

  const handleDocumentTypeChange = (value: string) => {
    setSelectedDocumentTypeId(value);
    // Reset sub-type field when document type changes
    form.setFieldsValue({ documentSubTypeId: undefined });
  };

  const handleFileChange = (info: any) => {
    const files = (info.fileList || [])
      .map((fileItem: any) => fileItem.originFileObj)
      .filter(Boolean);
    setSelectedFiles(files);
  };

  const handleUpload = async () => {
    try {
      const values = await form.validateFields();
      if (!selectedFiles.length) {
        showNotification("Please select at least one file to upload.", "error");
        return;
      }

      setUploading(true);
      try {
        await Promise.all(
          selectedFiles.map((selectedFile) => {
            const formData = new FormData();
            formData.append("staffId", staffId);
            formData.append("documentTypeId", values.documentTypeId);
            formData.append("documentSubTypeId", values.documentSubTypeId);
            if (relatedRecordType) formData.append("relatedRecordType", relatedRecordType);
            if (relatedRecordId) formData.append("relatedRecordId", relatedRecordId);
            formData.append("file", selectedFile);

            return Client.post(API_UPLOAD_DOCUMENT, formData, {
              // headers: { "Content-Type": "multipart/form-data" }, // Let Axios automatically set Content-Type for FormData
            });
          })
        );

        showNotification(
          selectedFiles.length === 1
            ? "File uploaded successfully."
            : "Files uploaded successfully.",
          "success"
        );
        onSubmit(values.documentTypeId);
        onClose();
      } catch (error) {
        console.error("Upload failed:", error);
        showNotification("File upload failed.", "error");
      } finally {
        setUploading(false);
      }
    } catch (errorInfo) {
      console.log("Validation Failed:", errorInfo);
      showNotification("Please fill in all required fields.", "error");
    }
  };

  const documentTypeOptions = documentTypes.map((dt) => ({
    value: dt.id,
    label: dt.documentTypeName,
  }));

  const documentSubTypeOptions = documentSubTypes.map((dst) => ({
    value: dst.id,
    label: dst.documentSubTypeName,
  }));

  return (
    <Modal
      title={title}
      open={visible}
      onCancel={onClose}
      footer={[
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        <Button
          key="upload"
          type="primary"
          onClick={handleUpload}
          loading={uploading}
        >
          Upload
        </Button>,
      ]}
    >
      <Form form={form} layout="vertical" initialValues={{ staffId }}>
        <Form.Item
          name="file"
          label="File"
          valuePropName="fileList"
          getValueFromEvent={(e) => (Array.isArray(e) ? e : e && e.fileList)}
          rules={[{ required: true, message: "Please select a file!" }]}
        >
          <Upload
            beforeUpload={() => false} // Prevent automatic upload
            onChange={handleFileChange}
            multiple
          >
            <Button icon={<UploadOutlined />}>Select Files</Button>
          </Upload>
        </Form.Item>

        <Form.Item
          name="documentTypeId"
          label="Document Type"
          rules={[{ required: true, message: "Please select a document type!" }]}
        >
          <Select
            placeholder="Select Document Type"
            options={documentTypeOptions}
            onChange={handleDocumentTypeChange}
            loading={documentTypes.length === 0} // Show loading if document types are still being fetched
          />
        </Form.Item>

        <Form.Item
          name="documentSubTypeId"
          label="Document Sub Type"
          rules={[{ required: true, message: "Please select a document sub type!" }]}
        >
          <Select
            placeholder="Select Document Sub Type"
            options={documentSubTypeOptions}
            disabled={!selectedDocumentTypeId || loadingDocumentSubTypes}
            loading={loadingDocumentSubTypes}
          >
            {loadingDocumentSubTypes && <Select.Option value="" disabled>Loading...</Select.Option>}
            {!loadingDocumentSubTypes && documentSubTypes.length === 0 && !!selectedDocumentTypeId && <Select.Option value="" disabled>No sub types found</Select.Option>}
          </Select>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default UploadDocumentModal;
